import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';
import { Link, Navigate } from 'react-router-dom';
import { Users, Search, TrendingUp, Mail, Loader2, AlertTriangle, DollarSign, ShieldCheck, Sparkles, Trash2, Beaker } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/apiBase';
import UserResumesModal from '../components/UserResumesModal';


const TIER_STYLES = {
  free: { bg: 'bg-[#f4f5f7]', text: 'text-[#4b5563]', label: 'Free' },
  basic: { bg: 'bg-[#dbeafe]', text: 'text-[#1e40af]', label: 'Basic' },
  pro: { bg: 'bg-[#fef3c7]', text: 'text-[#92400e]', label: 'Pro' },
};

const TierBadge = ({ tier }) => {
  const t = TIER_STYLES[tier] || TIER_STYLES.free;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold ${t.bg} ${t.text}`}>
      {t.label}
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const fmtPackage = (pkg) => {
  if (!pkg) return '—';
  const map = { pro: 'Pro', basic: 'Basic', free: 'Free', credits_10: '10 Credits', credits_50: '50 Credits' };
  return map[pkg] || pkg;
};

const fmtRelative = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 30) return `${diffDays}d ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  } catch {
    return '—';
  }
};

export default function AdminUsers() {
  useHideCookieBanner();
  const { user, loading: authLoading } = useAuth();
  const [q, setQ] = useState('');
  const [tier, setTier] = useState('all');
  // Sort axis: 'created_at' (signup cohort) | 'last_active' (engagement view).
  // The engagement view is the high-leverage one for client follow-ups —
  // shows who's actively using the product. Default stays on created_at
  // for muscle-memory compatibility with the previous behavior.
  const [sort, setSort] = useState('created_at');
  // Test-account filter — default excludes ascendqa.io / example.com /
  // test_* accounts. Toggle reveals them for debugging.
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [grantingEmail, setGrantingEmail] = useState(null);
  // User-detail "Resumes" inspector — opened by clicking the resume-count
  // cell in the table. Lets admins triage support tickets without a full
  // user-detail route. Powered by GET /api/admin/users/{user_id}/resumes.
  const [resumesUser, setResumesUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (tier !== 'all') params.set('tier', tier);
      params.set('sort', sort);
      params.set('include_test', String(includeTest));
      params.set('limit', '500');
      const res = await axios.get(`${API}/admin/users?${params.toString()}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [q, tier, sort, includeTest]);

  useEffect(() => {
    if (authLoading || !user) return;
    if (user.role !== 'admin') return;
    const t = setTimeout(load, 250); // debounce search
    return () => clearTimeout(t);
  }, [authLoading, user, load]);

  const handleGrantPro = useCallback(async (email) => {
    if (!window.confirm(
      `Grant Pro to ${email}?\n\n` +
      `• Marks email_verified = true\n` +
      `• Sets subscription_tier = pro\n` +
      `• Adds 99,999 credits (same as admin comp)\n` +
      `• Sends them a welcome email with sign-in instructions\n\n` +
      `If the account doesn't exist yet, it will be created as a passwordless magic-link account.`
    )) return;
    setGrantingEmail(email);
    try {
      const res = await axios.post(
        `${API}/admin/users/grant-pro`,
        { email, send_email: true },
        { withCredentials: true }
      );
      const { created, already_pro, email_sent, email_error, resend_id } = res.data;
      const msg = created
        ? `Created & upgraded ${email} to Pro`
        : already_pro
          ? `${email} was already Pro (credits + verified refreshed)`
          : `Upgraded ${email} to Pro`;
      if (email_sent) {
        toast.success(`${msg} — welcome email sent (id ${resend_id?.slice(0, 8) || '?'})`);
      } else if (email_error) {
        toast.error(`${msg} — BUT email failed: ${email_error}`);
      } else {
        toast.success(msg);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant Pro');
    } finally {
      setGrantingEmail(null);
    }
  }, [load]);

  const handleResendWelcome = useCallback(async (email) => {
    if (!window.confirm(`Re-send Pro welcome email to ${email}?\n\nDoes not change the account — only re-triggers the email.`)) return;
    setGrantingEmail(email);
    try {
      const res = await axios.post(
        `${API}/admin/users/resend-pro-welcome`,
        { email },
        { withCredentials: true }
      );
      const { email_sent, email_error, resend_id } = res.data;
      if (email_sent) {
        toast.success(`Welcome email re-sent to ${email} (id ${resend_id?.slice(0, 8) || '?'})`);
      } else {
        toast.error(`Email send failed: ${email_error || 'unknown error'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-send welcome');
    } finally {
      setGrantingEmail(null);
    }
  }, []);

  // Hard-delete an account. Backend enforces: admins can't be deleted,
  // Pro-tier users can't be deleted (paying-customer guardrail), and the
  // `confirm_email` field must match exactly — so we ask the operator to
  // re-type the email in a prompt as a typo-safety lock.
  const handleDeleteUser = useCallback(async (u) => {
    const typed = window.prompt(
      `Hard-delete ${u.email}?\n\n` +
      `This permanently removes the user and ALL their data:\n` +
      `  • resumes, applications, free ATS checks\n` +
      `  • leads, drip log, comp grants, audit links\n\n` +
      `This is IRREVERSIBLE. Backend will refuse for admins and Pro-tier accounts.\n\n` +
      `To confirm, retype the email exactly:`
    );
    if (typed === null) return; // cancelled
    if (typed.trim().toLowerCase() !== u.email.toLowerCase()) {
      toast.error("Email didn't match — deletion aborted.");
      return;
    }
    setGrantingEmail(u.email);
    try {
      const res = await axios.delete(
        `${API}/admin/users/${u.user_id}`,
        {
          withCredentials: true,
          data: { confirm_email: u.email, reason: 'admin_cleanup_test_account' },
        }
      );
      const { total_artifacts_deleted } = res.data;
      toast.success(`Deleted ${u.email} (${total_artifacts_deleted} artifacts removed)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete user');
    } finally {
      setGrantingEmail(null);
    }
  }, [load]);

  // Manually flip the is_test_account flag on a single user. Use case:
  // a real user with a test-style email (or vice versa) that the
  // pattern-match regex got wrong. Idempotent — POST twice = back to
  // original state.
  const handleToggleTestFlag = useCallback(async (u) => {
    const next = !u.is_test_account;
    const verb = next ? 'Mark as TEST account' : 'Mark as REAL account';
    if (!window.confirm(`${verb}?\n\n${u.email}\n\nThis only affects which admin dashboard view they appear in — no data is deleted.`)) return;
    setGrantingEmail(u.email);
    try {
      const res = await axios.post(
        `${API}/admin/users/${u.user_id}/toggle-test-flag`,
        {},
        { withCredentials: true }
      );
      toast.success(
        res.data.is_test_account
          ? `${u.email} now flagged as TEST — hidden from real-user views`
          : `${u.email} now flagged as REAL — visible in real-user views`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle test flag');
    } finally {
      setGrantingEmail(null);
    }
  }, [load]);

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading...</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const users = data?.users || [];
  const total = data?.total ?? 0;
  const paidCount = users.filter(u => u.subscription_tier && u.subscription_tier !== 'free').length;
  const totalRevenue = users.reduce((sum, u) => sum + (u.purchase?.total_paid || 0), 0);

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-users-page">
      <AdminReadinessBanner />
      <AdminSubNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7]">ADMIN</span>
            <h1 className="font-satoshi text-3xl font-black text-[#0f1115]">Registered Users</h1>
            <p className="text-sm text-[#6b7280]">All verified and unverified users, with subscription and purchase info.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/admin/funnel" className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-2" data-testid="link-funnel">
              <TrendingUp size={14} /> Funnel
            </Link>
            <Link to="/admin/broadcast" className="px-3 py-2 text-sm font-semibold bg-[#0f1115] text-white hover:bg-[#002fa7] transition-colors inline-flex items-center gap-2" data-testid="link-broadcast">
              <Mail size={14} /> Broadcast
            </Link>
          </div>
        </div>

        {/* Top metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="users-metrics">
          <div className="bg-white border border-[#e5e7eb] p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#6b7280] mb-1"><Users size={13} /> Total</div>
            <div className="font-satoshi text-2xl font-black text-[#0f1115]">{total.toLocaleString()}</div>
          </div>
          <div className="bg-white border border-[#e5e7eb] p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#6b7280] mb-1"><ShieldCheck size={13} /> Paid</div>
            <div className="font-satoshi text-2xl font-black text-[#0f1115]">{paidCount.toLocaleString()}</div>
            <div className="text-xs text-[#6b7280]">in this view</div>
          </div>
          <div className="bg-white border border-[#e5e7eb] p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#6b7280] mb-1"><DollarSign size={13} /> Revenue</div>
            <div className="font-satoshi text-2xl font-black text-[#0f1115]">${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="text-xs text-[#6b7280]">lifetime, in this view</div>
          </div>
          <div className="bg-white border border-[#e5e7eb] p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#6b7280] mb-1"><Users size={13} /> Showing</div>
            <div className="font-satoshi text-2xl font-black text-[#0f1115]">{users.length.toLocaleString()}</div>
            <div className="text-xs text-[#6b7280]">of {total.toLocaleString()}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#e5e7eb] p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="users-filters">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9ca3af]" />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email or name…"
              className="w-full pl-9 pr-3 py-2 border border-[#e5e7eb] text-sm focus:outline-none focus:border-[#002fa7]"
              data-testid="users-search"
            />
          </div>
          <div className="flex items-center gap-1">
            {[
              { id: 'all', label: 'All' },
              { id: 'free', label: 'Free' },
              { id: 'basic', label: 'Basic' },
              { id: 'pro', label: 'Pro' },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setTier(opt.id)}
                className={`px-3 py-2 text-sm font-semibold transition-colors ${tier === opt.id ? 'bg-[#002fa7] text-white' : 'bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7]'}`}
                data-testid={`filter-tier-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Sort axis toggle. Two options is intentionally the entire surface
              — more sort options encourage operator decision paralysis. */}
          <div className="flex items-center gap-1 border-l border-[#e5e7eb] pl-3 ml-1">
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#6b7280] mr-1">Sort</span>
            {[
              { id: 'created_at', label: 'Newest signup' },
              { id: 'last_active', label: 'Last active' },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setSort(opt.id)}
                className={`px-3 py-2 text-sm font-semibold transition-colors ${sort === opt.id ? 'bg-[#0f1115] text-white' : 'bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#0f1115]'}`}
                data-testid={`sort-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Test-account filter — same component as on /admin/acquisition.
              Default excludes test users (real-user signal); flip on to
              debug or restore the old behavior. */}
          <div className="flex items-center gap-1 border-l border-[#e5e7eb] pl-3 ml-1" data-testid="users-test-filter">
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#6b7280] mr-1">Data</span>
            <button
              onClick={() => setIncludeTest(false)}
              className={`px-3 py-2 text-sm font-semibold transition-colors ${!includeTest ? 'bg-[#0f1115] text-white' : 'bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#0f1115]'}`}
              data-testid="users-filter-real-only"
            >
              Real users
            </button>
            <button
              onClick={() => setIncludeTest(true)}
              className={`px-3 py-2 text-sm font-semibold transition-colors ${includeTest ? 'bg-[#0f1115] text-white' : 'bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#0f1115]'}`}
              data-testid="users-filter-include-test"
            >
              All (incl. tests)
            </button>
          </div>
        </div>

        {err && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 flex items-center gap-2" data-testid="users-error">
            <AlertTriangle size={16} /> {err}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-[#e5e7eb] overflow-x-auto" data-testid="users-table">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#6b7280]">
              <Loader2 size={18} className="animate-spin mr-2" /> Loading users…
            </div>
          ) : users.length === 0 ? (
            <div className="py-12 text-center text-sm text-[#6b7280]">No users match your filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#f4f5f7] text-[#6b7280] uppercase text-xs tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Email</th>
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-4 py-3 text-left font-semibold">Tier</th>
                  <th className="px-4 py-3 text-left font-semibold">Package</th>
                  <th className="px-4 py-3 text-right font-semibold">Paid</th>
                  <th className="px-4 py-3 text-right font-semibold" title="Number of resumes created">Resumes</th>
                  <th className="px-4 py-3 text-right font-semibold" title="Number of tracked applications">Apps</th>
                  <th className="px-4 py-3 text-left font-semibold">Last Active</th>
                  <th className="px-4 py-3 text-left font-semibold">Joined</th>
                  <th className="px-4 py-3 text-left font-semibold">Referral</th>
                  <th className="px-4 py-3 text-left font-semibold">Verified</th>
                  <th className="px-4 py-3 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e5e7eb]">
                {users.map(u => (
                  <tr key={u.user_id} className="hover:bg-[#f4f5f7] transition-colors" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-4 py-3 text-[#0f1115] font-mono text-xs" data-testid={`user-email-${u.user_id}`}>
                      {u.email}
                      {u.is_test_account && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] bg-[#fef3c7] text-[#92400e] border border-[#fde68a]"
                          title="Manually flagged as a test account"
                          data-testid={`test-badge-${u.user_id}`}
                        >
                          <Beaker size={9} /> TEST
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#0f1115]" data-testid={`user-name-${u.user_id}`}>{u.name || '—'}</td>
                    <td className="px-4 py-3"><TierBadge tier={u.subscription_tier || 'free'} /></td>
                    <td className="px-4 py-3 text-[#4b5563]">{fmtPackage(u.purchase?.last_package)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#0f1115]">
                      {u.purchase?.total_paid ? `$${u.purchase.total_paid.toFixed(2)}` : '—'}
                      {u.purchase?.purchase_count > 1 && (
                        <span className="ml-1 text-xs text-[#9ca3af]">×{u.purchase.purchase_count}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-[#0f1115]">
                      {(u.resume_count ?? 0) > 0 ? (
                        <button
                          type="button"
                          onClick={() => setResumesUser(u)}
                          className="text-[#002fa7] hover:underline font-mono"
                          data-testid={`view-resumes-${u.user_id}`}
                          title="View this user's resumes"
                        >
                          {u.resume_count}
                        </button>
                      ) : (
                        <span className="text-[#9ca3af]">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-[#0f1115]">{u.application_count ?? 0}</td>
                    <td className="px-4 py-3" title={u.last_active_at || 'No activity recorded'}>
                      {u.last_active_at ? (
                        <span className="text-[#6b7280]" data-testid="last-active">{fmtRelative(u.last_active_at)}</span>
                      ) : (
                        <span className="text-[#dc2626] font-semibold" data-testid="last-active-never">never</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#6b7280]">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-[#4b5563] text-xs">
                      {u.referral_used ? (
                        <span className="font-mono" title={`Referred ${fmtDate(u.referral_used.applied_at)}`}>{u.referral_used.code}</span>
                      ) : (
                        <span className="text-[#9ca3af]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.email_verified ? (
                        <span className="text-xs text-[#10b981] font-semibold">✓ Verified</span>
                      ) : (
                        <span className="text-xs text-[#9ca3af]">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {u.subscription_tier === 'pro' ? (
                          <button
                            onClick={() => handleResendWelcome(u.email)}
                            disabled={grantingEmail === u.email}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#92400e] bg-[#fef3c7] hover:bg-[#fde68a] transition-colors disabled:opacity-50"
                            data-testid={`resend-welcome-${u.user_id}`}
                            title="Re-send the Pro welcome email (does not change the account)"
                          >
                            {grantingEmail === u.email ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                            <span>Resend</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleGrantPro(u.email)}
                            disabled={grantingEmail === u.email}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-white bg-[#002fa7] hover:bg-[#001f70] transition-colors disabled:opacity-50"
                            data-testid={`grant-pro-${u.user_id}`}
                            title="Grant Pro + mark verified + email sign-in instructions"
                          >
                            {grantingEmail === u.email ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                            <span>Grant Pro</span>
                          </button>
                        )}
                        {/* Toggle the manual is_test_account flag. Visible
                            for ALL users (admin + free + pro) because edge
                            cases happen at every tier. */}
                        <button
                          onClick={() => handleToggleTestFlag(u)}
                          disabled={grantingEmail === u.email}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs transition-colors disabled:opacity-50 ${
                            u.is_test_account
                              ? 'text-[#92400e] bg-[#fef3c7] hover:bg-[#fde68a] border border-[#fde68a]'
                              : 'text-[#6b7280] bg-white border border-[#e5e7eb] hover:border-[#92400e]'
                          }`}
                          data-testid={`toggle-test-${u.user_id}`}
                          title={u.is_test_account ? 'Unflag as test account (will appear in Real Users view)' : 'Flag as test account (will be hidden from Real Users view)'}
                        >
                          <Beaker size={12} />
                          <span>{u.is_test_account ? 'Unflag' : 'Mark test'}</span>
                        </button>
                        {/* Hard-delete — hidden for admins and Pro users (backend would reject
                            those anyway; hiding the button just removes the misleading
                            affordance). Click triggers a re-type-the-email confirmation. */}
                        {u.role !== 'admin' && u.subscription_tier !== 'pro' && (
                          <button
                            onClick={() => handleDeleteUser(u)}
                            disabled={grantingEmail === u.email}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#dc2626] bg-white border border-[#e5e7eb] hover:bg-[#fef2f2] hover:border-[#dc2626] transition-colors disabled:opacity-50"
                            data-testid={`delete-user-${u.user_id}`}
                            title="Permanently delete this account and all its data"
                          >
                            {grantingEmail === u.email ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-xs text-[#9ca3af] mt-3">
          Showing up to 500 users. Purchases pulled from paid Stripe transactions only.
        </p>
      </div>
      {resumesUser && (
        <UserResumesModal user={resumesUser} onClose={() => setResumesUser(null)} />
      )}
    </div>
  );
}
